import { useId, type CSSProperties, type InputHTMLAttributes } from 'react';

import { cx } from '../../lib/cx';

import styles from './Slider.module.css';

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** The setting it changes: its name. */
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** How the value is shown beside the name ("40 %"); without it, the number itself. */
  format?: (value: number) => string;
  /** The label is read by assistive technology but not shown: where a heading names it. */
  hideLabel?: boolean;
}

/**
 * A value on a scale, set by dragging or with the arrows: the volume, a tempo. Its
 * name and its value are above it; the track fills up to the thumb.
 */
export function Slider({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  format = String,
  hideLabel = false,
  className,
  id: given,
  ...rest
}: SliderProps) {
  const generated = useId();
  const id = given ?? generated;
  const share = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className={cx(styles.slider, className)}>
      <div className={cx(styles.head, hideLabel && styles.hiddenLabel)}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        <output htmlFor={id} className={styles.value}>
          {format(value)}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={format(value)}
        onChange={(event) => onChange(Number(event.target.value))}
        className={styles.input}
        style={{ '--hs-slider-fill': `${share}%` } as CSSProperties}
        {...rest}
      />
    </div>
  );
}
